import React, { PropsWithChildren, useCallback, useEffect, useId, useRef, useState } from "react";

import { Button } from "@app/scripts/react-general-components/Button";
import { Portal } from "@app/scripts/react-general-components/Portal";
import { activateModal } from "@app/scripts/utils/modal";

type Props = {
  heading: string;
  secondaryHeading?: string;
  opensFrom?: "left" | "right" | "bottom";
  customButton?: React.FunctionComponent<{
    toggleOpen: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    open: boolean;
    triggerProps: React.AriaAttributes;
  }>;
  drawerHeaderButton?: React.FunctionComponent;
  drawerClassName?: string;
  destroyOnHide?: boolean;
  preventScroll?: boolean;
  inline?: boolean;
  disableOverlay?: boolean;
};

export const Drawer: React.FC<PropsWithChildren<Props>> = (props) => {
  const {
    heading,
    customButton,
    destroyOnHide = true,
    preventScroll = true,
    inline = false,
    opensFrom,
    drawerClassName,
    drawerHeaderButton,
  } = props;

  const [open, setOpen] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);
  const openRef = useRef(false);
  const opener = useRef<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const dialogId = useId();

  const close = useCallback(() => {
    openRef.current = false;
    setActive(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 200);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const show = useCallback((e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e?.preventDefault();
    if (openRef.current) return;
    opener.current = e?.currentTarget || document.activeElement as HTMLElement;
    openRef.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setActive(true), 50);
    setOpen(true);
  }, []);

  const toggleOpen = useCallback((e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (openRef.current) close();
    else show(e);
  }, [close, show]);

  const triggerProps: React.AriaAttributes = {
    "aria-haspopup": "dialog", "aria-expanded": open, "aria-controls": dialogId,
  };
  const trigger = customButton?.({ toggleOpen: show, open, triggerProps });
  const directTrigger = React.isValidElement(trigger) &&
    (typeof trigger.type !== "string" || ["button", "a", "summary", "input"].includes(trigger.type));

  return (
    <>
      {customButton ? (
        directTrigger ? React.cloneElement(trigger as React.ReactElement, triggerProps) : trigger
      ) : (
        <Button
          color="outline"
          variant="secondary"
          className="fullwidth swiper-no-swiping"
          ariaLabel={heading}
          onClick={show}
          label={heading}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={dialogId}
        />
      )}

      {!open && destroyOnHide ? null : inline ? (
        <div
          className={`ui-drawer__inline ${drawerClassName ?? ""} ${active ? "active" : ""} from--${
            opensFrom ?? "right"
          }`}
        >
          <DrawerContent
            {...props}
            open={open}
            toggleOpen={toggleOpen}
            active={active}
            drawerHeaderButton={drawerHeaderButton}
            dialogId={dialogId}
            opener={opener}
            close={close}
          />
        </div>
      ) : (
        <Portal>
          <DrawerContent
            {...props}
            open={open}
            toggleOpen={toggleOpen}
            active={active}
            drawerHeaderButton={drawerHeaderButton}
            dialogId={dialogId}
            opener={opener}
            close={close}
          />
        </Portal>
      )}
    </>
  );
};

type DrawerContentProps = Props & {
  toggleOpen: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  open: boolean;
  active: boolean;
  dialogId: string;
  opener: React.MutableRefObject<HTMLElement | null>;
  close: () => void;
};

const DrawerContent: React.FC<PropsWithChildren<DrawerContentProps>> = ({
  drawerClassName,
  opensFrom,
  heading,
  secondaryHeading,
  toggleOpen,
  open,
  active,
  drawerHeaderButton,
  disableOverlay,
  children,
  dialogId,
  opener,
  close,
  preventScroll = true,
}) => {
  const dialog = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!open) return;
    let release: (() => void) | undefined;
    const frame = requestAnimationFrame(() => {
      if (dialog.current && title.current) {
        release = activateModal(dialog.current, title.current, close, opener.current, preventScroll);
      }
    });
    return () => { cancelAnimationFrame(frame); release?.(); };
  }, [open, close, preventScroll]);
  return (
  <>
    {open && !disableOverlay ? (
      <div data-modal-overlay={dialogId} aria-hidden="true" className={`ui-drawer__overlay ${drawerClassName ?? ""}`} onClick={close} />
    ) : null}

    <div ref={dialog} id={dialogId} role="dialog" aria-modal="true" aria-labelledby={`${dialogId}-title`} tabIndex={-1} hidden={!open} className={`ui-drawer ${active ? "active" : ""} from--${opensFrom ?? "right"} ${drawerClassName ?? ""}`}>
      <div className="ui-drawer__heading page-width text-brand-dark-grey">
        <div className="ui-drawer__heading-title-wrap">
          <h2 ref={title} id={`${dialogId}-title`} tabIndex={-1} className="h3 text-brand-dark-grey bold">
            {heading} {secondaryHeading && <span>{secondaryHeading}</span>}
          </h2>
          {drawerHeaderButton?.({})}
        </div>
        <button type="button" className="ui-drawer__close" aria-label="Close" onClick={close}>
          <span className="button button--text hidden ui-drawer__button-text">Close</span>
          <ui-icon width="16" height="16" name="icon-close-large"></ui-icon>
        </button>
      </div>

      {React.Children.map(children, (child) => {
        return React.cloneElement(child as React.ReactElement, { toggleOpen });
      })}
    </div>
  </>
  );
};
